import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService, LoginResponse } from '@/services/api-service';
import { WEB_ARCHIVE_ACCESS_TOKEN } from '@/web-archive/WebArchiveGate';

interface AuthContextType {
  user: LoginResponse['user'] | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (emailOrUsername: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<LoginResponse['user'] | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load auth state from localStorage on mount
  useEffect(() => {
    // Check if we're coming from a page unload (reload or close)
    const wasUnloading = sessionStorage.getItem('wasUnloading');
    if (wasUnloading === 'true') {
      sessionStorage.removeItem('wasUnloading');
      sessionStorage.removeItem('logoutTimestamp');
      
      // Clear local storage immediately
      const refreshToken = localStorage.getItem('refreshToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('current_engagement_id');
      localStorage.removeItem('sharepoint_config');
      setUser(null);
      setToken(null);
      setIsLoading(false);
      
      // Logout the old session in background (don't block)
      if (refreshToken) {
        apiService.logout(refreshToken, false).catch(() => {
          // Ignore errors
        });
      }
      return;
    }

    // Try to restore session
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        // Local ZIP web viewer: no remote auth server
        if (storedToken === WEB_ARCHIVE_ACCESS_TOKEN) {
          return;
        }
        // Verify token in background (don't block)
        apiService.getCurrentUser()
          .then((currentUser) => {
            setUser(currentUser);
            localStorage.setItem('user', JSON.stringify(currentUser));
          })
          .catch(() => {
            // Token invalid, clear storage
            setUser(null);
            setToken(null);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('current_engagement_id');
            localStorage.removeItem('sharepoint_config');
          });
      } catch (error) {
        console.error('Error loading auth state:', error);
        setUser(null);
        setToken(null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('current_engagement_id');
        localStorage.removeItem('sharepoint_config');
      }
    }
    
    // Always set loading to false immediately
    setIsLoading(false);
  }, []);


  // Handle app close/reload - call backend logout
  useEffect(() => {
    let logoutCalled = false;
    
    const performLogout = async (eventName: string, isElectronClose: boolean = false) => {
      try {
        // Prevent multiple logout calls
        if (logoutCalled) {
          console.log(`[Logout] Already called, ignoring ${eventName}`);
          return;
        }
        logoutCalled = true;
        
        console.log(`[Logout] ${eventName} event fired, attempting logout...`);

        // Offline web archive (Railway / dev:web): no remote session to tear down
        if (localStorage.getItem('accessToken') === WEB_ARCHIVE_ACCESS_TOKEN) {
          console.log('[Logout] Web archive session — skipping unload logout');
          return;
        }
        
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          console.warn('[Logout] No refresh token found');
          // Still notify Electron that logout is complete
          if (isElectronClose && window.electronWindow?.sendLogoutComplete) {
            window.electronWindow.sendLogoutComplete();
          }
          return;
        }

        // For reload detection: check if we're navigating away (reload) vs closing
        // We'll set a flag and check on next page load
        // If the flag exists on next load, it was a reload
        // If flag doesn't exist but we have tokens, it was a close
        sessionStorage.setItem('wasUnloading', 'true');
        sessionStorage.setItem('logoutTimestamp', Date.now().toString());
        
        // Always call logout for both reload and close
        // For Electron close, use normal fetch (not sendBeacon) so we can await it
        if (isElectronClose) {
          console.log('[Logout] Electron close detected, using fetch (not sendBeacon)...');
          try {
            await apiService.logout(refreshToken, false);
            console.log('[Logout] Logout complete for Electron close');
          } catch (error) {
            console.error('[Logout] Error in logout call:', error);
          } finally {
            // Notify Electron that logout is complete
            if (window.electronWindow?.sendLogoutComplete) {
              window.electronWindow.sendLogoutComplete();
            }
          }
        } else {
          // Use sendBeacon for browser reload/close
          console.log('[Logout] Calling apiService.logout with sendBeacon...');
          apiService.logout(refreshToken, true).catch((error) => {
            console.error('[Logout] Error in logout call:', error);
          });
        }
      } catch (error) {
        console.error('[Logout] Error in performLogout:', error);
      }
    };

    // Handle Electron window close request (only if Electron is available)
    try {
      if (typeof window !== 'undefined' && window.electronWindow?.onLogoutRequest) {
        console.log('[Logout] Registering Electron logout request handler...');
        window.electronWindow.onLogoutRequest(() => {
          console.log('[Logout] Electron requested logout before close');
          performLogout('electron-close', true);
        });
      }
    } catch (error) {
      console.warn('[Logout] Error registering Electron handler (non-critical):', error);
    }

    // Use multiple events to catch all scenarios
    // pagehide is most reliable for modern browsers (works for both reload and close)
    const handlePageHide = (e: PageTransitionEvent) => {
      // If page is being persisted (back/forward cache), we don't need to logout
      if (e.persisted) {
        console.log('[Logout] Page is being persisted (bfcache), skipping logout');
        return;
      }
      performLogout('pagehide', false);
    };

    // beforeunload fires before pagehide (for both reload and close)
    const handleBeforeUnload = () => {
      performLogout('beforeunload', false);
    };

    // unload event as additional fallback (fires after beforeunload, before pagehide)
    const handleUnload = () => {
      performLogout('unload', false);
    };

    // Listen for multiple events to ensure logout is called
    // Order: beforeunload -> unload -> pagehide
    // We listen to all to catch different browser behaviors
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    window.addEventListener('pagehide', handlePageHide);

    console.log('[Logout] Event listeners registered for beforeunload, unload, and pagehide');

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  const login = async (emailOrUsername: string, password: string) => {
    try {
      const response = await apiService.login(emailOrUsername, password);
      setUser(response.user);
      setToken(response.accessToken);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = useCallback(async () => {
    // Call backend logout API to mark user as logged out
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await apiService.logout(refreshToken);
      }
    } catch (error) {
      // Log error but continue with local logout
      console.error('Error during logout API call:', error);
    }

    // Clear local state and storage
    setUser(null);
    setToken(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('current_engagement_id');
    localStorage.removeItem('sharepoint_config');
  }, []);

  const refreshUser = async () => {
    try {
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
      localStorage.setItem('user', JSON.stringify(currentUser));
    } catch (error) {
      console.error('Error refreshing user:', error);
      logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user && !!token,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

